/*
 * Cart drawer behaviour.
 *
 * Opens when:
 *   - the product page fires "cart:added" (it passes along a freshly rendered drawer)
 *   - someone clicks a link marked [data-cart-link] (the header "cart" link)
 *
 * Quantity / remove clicks: change the cart, ask Shopify for a fresh copy of
 * this section, swap it in. (The countdown timer is handled in theme.js.)
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-drawer-body]');
    this.panel = this.querySelector('.cart-drawer__panel');

    // Header "cart" link opens the drawer instead of going to /cart
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-cart-link]');
      if (!link) return;
      event.preventDefault();
      this.open();
    });

    // Added from a product page
    document.addEventListener('cart:added', (event) => {
      if (event.detail?.html) this.render(event.detail.html);
      this.open();
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();

      const qtyButton = event.target.closest('[data-cart-qty]');
      if (qtyButton) {
        const line = Number(qtyButton.closest('[data-line]').dataset.line);
        this.updateQuantity(line, Number(qtyButton.dataset.cartQty));
      }

      // ‹ › on the bundle bar: show the previous/next suggested piece
      const step = event.target.closest('[data-bundle-cycle-step]');
      if (step) {
        const items = [...step.closest('[data-bundle-cycle]').querySelectorAll('[data-drawer-add-row]')];
        const current = items.findIndex((item) => !item.hidden);
        items[current].hidden = true;
        items[(current + Number(step.dataset.bundleCycleStep) + items.length) % items.length].hidden = false;
      }

      // "add" on a piece in the drawer (complete your bundle)
      const quickAdd = event.target.closest('[data-drawer-add]');
      if (quickAdd) {
        const row = quickAdd.closest('[data-drawer-add-row]');
        quickAdd.disabled = true;
        this.addItem(Number(row.querySelector('[data-drawer-add-variant]').value));
      }
    });

    // Bundle bar: show the chosen colour's photo
    this.addEventListener('change', (event) => {
      const select = event.target.closest('[data-drawer-add-variant]');
      const image = select?.closest('[data-drawer-add-row]').querySelector('[data-drawer-add-image]');
      const src = select?.selectedOptions[0].dataset.image;
      if (!image || !src) return;
      image.src = src;
      image.removeAttribute('srcset');
    });

    document.addEventListener('keydown', (event) => {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.keepFocusInside(event);
    });
  }

  open() {
    this.returnFocusTo = document.activeElement; // e.g. the add to cart button
    this.classList.add('is-open');
    document.body.classList.add('drawer-open'); // stops the page behind from scrolling
    this.panel.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    this.returnFocusTo?.focus?.(); // keyboard users land back where they were
  }

  // While open, Tab cycles through the drawer's buttons and links instead of the page behind it
  keepFocusInside(event) {
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async addItem(id) {
    this.classList.add('is-loading');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: ['cart-drawer'] }),
      });
      const result = await response.json();
      if (!response.ok) {
        this.showError(result.description || result.message || 'could not add to your cart');
        return;
      }
      this.render(result.sections['cart-drawer']);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch {
      this.showError('could not add to your cart');
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async updateQuantity(line, quantity) {
    this.classList.add('is-loading');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'] }),
      });
      const result = await response.json();

      if (!response.ok) {
        this.showError(result.description || result.message || 'could not update your cart');
        return;
      }

      this.render(result.sections['cart-drawer']);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: result.item_count } }));
    } catch {
      this.showError('could not update your cart');
    } finally {
      this.classList.remove('is-loading');
    }
  }

  // Swap in the new drawer contents Shopify rendered for us
  render(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-body]');
    if (fresh) this.body.innerHTML = fresh.innerHTML;
  }

  showError(message) {
    const error = this.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message.toLowerCase();
    error.hidden = false;
  }
}

customElements.define('cart-drawer', CartDrawer);
